/**
 * Campo de formulário do painel (F1.8) — rótulo, controle, ajuda e erro sempre juntos.
 *
 * Regras que este arquivo carrega:
 *  - **todo campo tem rótulo visível ligado ao controle** (`label htmlFor`): placeholder não é
 *    rótulo — some quando a pessoa digita e o leitor de tela não o trata como nome do campo;
 *  - piso de legibilidade de formulário = 14 px (`--font-size-formulario`, decisão 1.9.3): nenhum
 *    texto de campo pode ficar menor;
 *  - "obrigatório" é dito **por escrito**, não por um asterisco que só quem já usa sistema entende;
 *  - erro é anunciado na hora (`role="alert"`) e o controle fica marcado como inválido;
 *  - campo que a pessoa não pode editar não some: fica só leitura, com a frase do porquê
 *    (mesma regra do `Botao` — a frase vem de `porQueNaoPode()` de `auth/sessao.ts`).
 */
import type { CSSProperties } from 'react';
import { useId } from 'react';

export type TipoCampo =
  'texto' | 'email' | 'senha' | 'numero' | 'telefone' | 'data' | 'lista' | 'longo';

export interface OpcaoCampo {
  valor: string;
  rotulo: string;
}

export interface CampoProps {
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  tipo?: TipoCampo | undefined;
  id?: string | undefined;
  nome?: string | undefined;
  /** Explicação curta abaixo do rótulo (ex.: "usamos para avisar sobre pendências"). */
  ajuda?: string | undefined;
  /** Mensagem de erro em português de operação — nunca código nem nome de campo do banco. */
  erro?: string | null | undefined;
  obrigatorio?: boolean | undefined;
  /** Frase de `porQueNaoPode()`: preenchida = campo só leitura + explicação visível. */
  motivoDesabilitado?: string | null | undefined;
  /** Só para `tipo="lista"`. */
  opcoes?: readonly OpcaoCampo[] | undefined;
  placeholder?: string | undefined;
  autoComplete?: string | undefined;
  /** Altura em linhas do `tipo="longo"`. */
  linhas?: number | undefined;
}

/** Tipo do produto → tipo do HTML. A tela fala a língua do produto, não a do navegador. */
const TIPO_HTML: Record<Exclude<TipoCampo, 'lista' | 'longo'>, string> = {
  texto: 'text',
  email: 'email',
  senha: 'password',
  numero: 'number',
  telefone: 'tel',
  data: 'date',
};

export function Campo({
  rotulo,
  valor,
  aoMudar,
  tipo = 'texto',
  id,
  nome,
  ajuda,
  erro,
  obrigatorio = false,
  motivoDesabilitado = null,
  opcoes,
  placeholder,
  autoComplete,
  linhas = 4,
}: CampoProps) {
  const idGerado = useId();
  const idCampo = id ?? `campo-${idGerado}`;
  const idAjuda = `${idCampo}-ajuda`;
  const idErro = `${idCampo}-erro`;
  const idMotivo = `${idCampo}-motivo`;

  const temErro = typeof erro === 'string' && erro.trim() !== '';
  const soLeitura = typeof motivoDesabilitado === 'string' && motivoDesabilitado.trim() !== '';

  const descritores = [ajuda ? idAjuda : null, temErro ? idErro : null, soLeitura ? idMotivo : null]
    .filter((parte): parte is string => parte !== null)
    .join(' ');

  const estiloControle: CSSProperties = {
    width: '100%',
    fontSize: 'var(--font-size-formulario)',
    lineHeight: 'var(--altura-linha-normal)',
    color: 'var(--cor-texto)',
    background: soLeitura ? 'var(--cor-superficie-3)' : 'var(--cor-superficie)',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: temErro ? 'var(--cor-perigo)' : 'var(--cor-linha-forte)',
    borderRadius: 'var(--raio-sm)',
    padding: 'var(--esp-2) var(--esp-3)',
    minHeight: 'var(--alvo-toque-min)',
  };

  const comuns = {
    id: idCampo,
    name: nome ?? idCampo,
    style: estiloControle,
    'aria-describedby': descritores === '' ? undefined : descritores,
    'aria-invalid': temErro || undefined,
    'aria-required': obrigatorio || undefined,
    'aria-disabled': soLeitura || undefined,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--esp-1)' }}>
      <label
        htmlFor={idCampo}
        style={{
          fontSize: 'var(--font-size-formulario)',
          fontWeight: 'var(--peso-medio)',
          color: 'var(--cor-texto)',
        }}
      >
        {rotulo}
        {/* Dito por extenso: "obrigatório" é informação, não símbolo. */}
        {obrigatorio ? (
          <span style={{ color: 'var(--cor-texto-suave)', fontWeight: 'var(--peso-normal)' }}>
            {' '}
            (obrigatório)
          </span>
        ) : null}
      </label>

      {ajuda ? (
        <p
          id={idAjuda}
          style={{
            margin: 0,
            color: 'var(--cor-texto-suave)',
            fontSize: 'var(--font-size-formulario)',
          }}
        >
          {ajuda}
        </p>
      ) : null}

      {tipo === 'lista' ? (
        // O `select` não tem "só leitura" no HTML; quando a pessoa não pode escolher, ele fica
        // desabilitado e a frase do porquê aparece logo abaixo, sempre visível.
        <select
          {...comuns}
          value={valor}
          disabled={soLeitura}
          onChange={(evento) => aoMudar(evento.target.value)}
        >
          <option value="">Selecione…</option>
          {(opcoes ?? []).map((opcao) => (
            <option key={opcao.valor} value={opcao.valor}>
              {opcao.rotulo}
            </option>
          ))}
        </select>
      ) : tipo === 'longo' ? (
        <textarea
          {...comuns}
          value={valor}
          rows={linhas}
          readOnly={soLeitura}
          placeholder={placeholder}
          onChange={(evento) => aoMudar(evento.target.value)}
        />
      ) : (
        // `readOnly` em vez de `disabled`: o campo continua alcançável pelo teclado, então a
        // explicação do porquê chega a quem navega sem mouse.
        <input
          {...comuns}
          type={TIPO_HTML[tipo]}
          value={valor}
          readOnly={soLeitura}
          placeholder={placeholder}
          autoComplete={autoComplete}
          onChange={(evento) => aoMudar(evento.target.value)}
        />
      )}

      {soLeitura ? (
        <p
          id={idMotivo}
          style={{
            margin: 0,
            color: 'var(--cor-texto-suave)',
            fontSize: 'var(--font-size-formulario)',
          }}
        >
          {motivoDesabilitado}
        </p>
      ) : null}

      {temErro ? (
        <p
          id={idErro}
          role="alert"
          style={{
            margin: 0,
            color: 'var(--cor-perigo)',
            fontSize: 'var(--font-size-formulario)',
            fontWeight: 'var(--peso-medio)',
          }}
        >
          {erro}
        </p>
      ) : null}
    </div>
  );
}
